import { NgIf } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { StepRecom } from '../../interface/step-recom';
import { StepRecomPicture } from '../../interface/step-recom-picture';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { StepRecomPictureService } from '../../service/step-recom-picture.service';
import { FileService } from '../../../../common/file/service/file.service';

@Component({
  selector: 'dpi-step-recom-picture',
  imports: [MatCardModule, MatIconModule, MatButtonModule, MatToolbarModule, MatTooltipModule, MatPaginatorModule, NgIf],
  templateUrl: './step-recom-picture.component.html',
  styleUrl: './step-recom-picture.component.scss'
})
export class StepRecomPictureComponent implements OnInit, OnDestroy {
  stepRecom: StepRecom;
  listDiagram: StepRecomPicture[] = [];
  imgFromApi: string = '';
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;

  pageLength = 0;
  pageSize = 1;
  pageIndex = 0;
  pageSizeOptions = [1];
  hidePageSize = true;
  showPageSizeOptions = false;
  showFirstLastButtons = true;
  disabled = false;
  pageEvent: PageEvent | undefined;
  handlePageEvent(e: PageEvent) {
    this.pageEvent = e;
    this.pageLength = e.length;
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    this.reloadImages();
  }
  
  // 配置项
  allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']; // 允许的文件类型
  maxSizeMB = 100; // 最大 100MB

  // 状态
  selectedFile: File | null = null;
  isDragging = false;
  uploadError = '';

  constructor(private notifyService: StateNotifyService, private stepRecomPictureService: StepRecomPictureService, private fileService: FileService) {
    this.stepRecom = this.notifyService.getSelectedStepRecom();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.stepRecomSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.stepRecom = model;
        this.loadListDiagram(model.id, true);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
        this.resetStatus();
      });
  }

  loadListDiagram(stepRecomId: number, displayFromFirstOne: boolean) {
    this.stepRecomPictureService.getAllPicturesOfStepRecom(stepRecomId) //Note: here must be StepRecomId
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.listDiagram = res;
          this.listSize.emit(this.listDiagram.length);
          if (displayFromFirstOne) {
            this.pageIndex = 0;
          } else {
            this.pageIndex = this.listDiagram.length - 1;
          }
          this.reloadImages();
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.listDiagram = [];
        }
      });
  }

  reloadImages() {
    this.pageLength = this.listDiagram.length;
    if (this.pageLength > 0) {
      const diagramRecord = this.listDiagram[this.pageIndex];

      this.stepRecomPictureService.getStepRecomPicture(diagramRecord.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (img: any) => {
            this.imgFromApi = this.fileService.arrayBufferToBase64(img);
          },
          error: (error: any) => {
            console.error('Error: ', error);
            this.imgFromApi = '';
          }
        });

    } else {
      this.imgFromApi = '';
    }

  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      //this.fileName = file.name;
      this.stepRecomPictureService.fileUpload(this.stepRecom.id, file).subscribe({
        next: res => {
          this.loadListDiagram(this.stepRecom.id, false);
        },
        error: err => {
          console.log(err);
        }
      });
    }
  }

  deleteStepRecomPicture() {
    if (this.pageLength > 0) {
      const diagramRecord = this.listDiagram[this.pageIndex];
      this.stepRecomPictureService.deleteStepRecomPicture(diagramRecord.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (res: any) => {
            this.loadListDiagram(this.stepRecom.id, true);
          },
          error: (error: any) => {
            console.error('Error: ', error);
          }
        });
    }
  }

  // ------------------------------
  // 拖放事件
  // ------------------------------
  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave() {
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.validateAndSetFile(files[0]);
    }
  }

  // ------------------------------
  // 文件验证与上传
  // ------------------------------
  validateAndSetFile(file: File) {
    this.resetStatus();

    // 检查类型
    if (!this.allowedTypes.includes(file.type)) {
      this.uploadError = '不支持此文件类型';
      return;
    }

    // 检查大小
    const maxSize = this.maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError = `文件不能超过 ${this.maxSizeMB}MB`;
      return;
    }

    this.selectedFile = file;

    if (file) {
      this.stepRecomPictureService.fileUpload(this.stepRecom.id, file).subscribe({
        next: res => {
          this.loadListDiagram(this.stepRecom.id, false);
        },
        error: err => {
          console.log(err);
        }
      });
    }

  }

  // 重置
  resetStatus() {
    this.selectedFile = null;
    this.uploadError = '';
  }

}
